import { useCallback, useState } from 'react';
import { apiFetch } from '@/lib/api-client';

/**
 * 5a Commit 3 · create-order wire hook.
 *
 * Endpoint: `POST /api/book/orders` — controller at
 * `src/book/create-order/create-order.controller.ts`. Auth-required
 * (JWT). Response `{ orderId: string }`. On success the server has
 * created the `BookOrder` row and transitioned it to
 * `light_gate_pending`; the client navigates to `/book/:orderId` to
 * enter the status-driven dispatch.
 *
 * ═══ PAYLOAD ═══════════════════════════════════════════════════════
 *
 * Mirrors `CreateBookOrderDto` at
 * `src/book/create-order/dto/create-book-order.dto.ts:116`.
 *
 * `sizeVariant` is REQUIRED by the DTO but the Konfigurator does NOT
 * collect it — Bau-Spez v3.1 §6 line 367 places Buchart + Größe in
 * Schritt 5 (Bestellung, Phase 6). Commit 3 sends `'standard'` as a
 * placeholder so the row can be created; Phase 6's Bestellung screen
 * updates the row before print. Tracker
 * `[book-create-order-size-variant-placeholder]` records the
 * dependency.
 *
 * `stadt` is null in Commit 3 — Textdatei v2 §Schritt 1 does not ask
 * for a Schauplätze prose today (§A3.6 is captured elsewhere in the
 * spec but does not have a form field yet in the Konfigurator).
 * Placeholder-safe: the DTO accepts `null`, and downstream the
 * resolver's null-stadt guard suppresses resolver calls for scenes
 * without a real place.
 */

/**
 * Block 2 commit 1 (Sebastian ruling 2026-08-31) — Gattung values
 * per person. Wording verbatim: "Person" / "Tier". Kuscheltier is
 * P-97 and NOT in this commit.
 */
export type CastPersonType = 'person' | 'tier';

/**
 * Block 2 commit 1 (Sebastian ruling 2026-08-31) — Geschlecht values
 * per person. Wording verbatim: "männlich, weiblich, keine Angabe".
 * Asked for BOTH Person and Tier — the pronoun ("er" or "sie") is
 * needed by the story text, not only by the outfit chain.
 */
export type CastGeschlecht = 'maennlich' | 'weiblich' | 'keineAngabe';

export interface CreateBookOrderCastPayload {
  readonly key: string;
  readonly name: string;
  readonly ageYears: number;
  readonly facePhotoKey: string;
  readonly fullBodyPhotoKey?: string;
  // Block 2 commit 1 (Sebastian ruling 2026-08-31) — new required
  // per-person fields. Field order on the row: Gattung → Name → Alter
  // → Geschlecht. `personenNotiz` is optional free text (200-char cap
  // enforced by DTO).
  readonly personType: CastPersonType;
  readonly geschlecht: CastGeschlecht;
  readonly personenNotiz?: string;
  /**
   * Position-2 Schritt B (Sebastian ruling 2026-09-11) — concrete
   * species when personType === 'tier'. REQUIRED at the DTO level
   * for tier entries (backend @ValidateIf), OMITTED for person
   * entries. The PWA Personen tier-branch input gates submit until
   * this is non-empty, so the payload builder emits it only in that
   * state; a person entry never carries the field.
   */
  readonly species?: string;
}

export interface CreateBookOrderPayload {
  readonly description: string;
  readonly sizeVariant: 'kompakt' | 'standard' | 'gross';
  readonly stadt: string | null;
  readonly cast: readonly CreateBookOrderCastPayload[];
  readonly schauplaetze?: string;
  readonly language?: string;
  /**
   * Block 2 commit 1 (Sebastian ruling 2026-08-31) — optional book-
   * level title wish (40-char cap enforced by DTO). Non-empty seeds
   * `book_orders.story_text.title` at creation; the anchor-title
   * service's idempotency guard then skips generation. Empty →
   * anchor-title fires normally (fallback preserved).
   */
  readonly titelwunsch?: string;
  /**
   * PROTOTYPE – feat/story-step-stations-prototype (Sebastian GO
   * 2026-10-08 round 2). Which card the customer tapped on step 1
   * of the Geschichtenschritt. Optional on the wire so an old PWA
   * build still validates. Persisted at BookOrder.intake.raw.storyKind.
   */
  readonly storyKind?: 'erlebt' | 'erfunden';
  /**
   * Meta Conversions API attribution snapshot captured at the
   * moment the overlay fires create-order (2026-10-30). Backend
   * persists it on BookOrder.meta_* columns consent-gated for IP +
   * UA. The ViewContent listener later re-reads these columns at
   * preview_ready.
   */
  readonly meta?: {
    readonly consentMarketing: boolean;
    readonly fbc?: string;
    readonly fbp?: string;
  };
}

export interface CreateBookOrderResponse {
  readonly orderId: string;
}

export interface UseCreateBookOrder {
  readonly submit: (payload: CreateBookOrderPayload) => Promise<CreateBookOrderResponse>;
  readonly submitting: boolean;
}

export function useCreateBookOrder(): UseCreateBookOrder {
  const [submitting, setSubmitting] = useState(false);

  const submit = useCallback(
    async (payload: CreateBookOrderPayload): Promise<CreateBookOrderResponse> => {
      setSubmitting(true);
      try {
        const res = await apiFetch<CreateBookOrderResponse>('/api/book/orders', {
          method: 'POST',
          body: payload,
        });
        return res;
      } finally {
        setSubmitting(false);
      }
    },
    [],
  );

  return { submit, submitting };
}
